import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { faRoute } from "@fortawesome/pro-solid-svg-icons/faRoute";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import notification from "antd/es/notification";
import { defineMessages, useIntl } from "react-intl";

import { Company, Person } from "@mapmycustomers/shared/types/entity";
import Route from "@mapmycustomers/shared/types/entity/Route";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Button, Modal } from "@mapmycustomers/ui";

import { isGeocodeMmcLimitReached, isGeocodeOrgLimitReached } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { MAX_ROUTE_STOPS } from "@app/util/consts";
import showGeocodeLimitAlertModal from "@app/util/geocodeLimitAlert/showGeocodeLimitAlertModal";
import itemToValueLabel from "@app/util/ui/itemToValueLabel";

import SelectField from "../input/SelectField";

import CreateRouteModal from "./CreateRouteModal";
import {
  getRoutes,
  getSelectedRouteId,
  isAdding,
  isCreateRouteModalVisible,
  isInitializing,
} from "./store";
import {
  addToRoute,
  filterRoutes,
  initialize,
  selectRoute,
  showCreateRouteModal,
} from "./store/actions";

const messages = defineMessages({
  add: {
    id: "addToRouteModal.footer.add",
    defaultMessage: "Add to Route",
    description: "Add button in a footer of Add To Route modal",
  },
  create: {
    id: "addToRouteModal.footer.create",
    defaultMessage: "+ Create New Route",
    description: "Create New Route button in a footer of Add To Route modal",
  },
  header: {
    id: "addToRouteModal.header",
    defaultMessage: "Add Records to Route",
    description: "Header of Add To Route modal",
  },
  placeholder: {
    id: "addToRouteModal.body.select.placeholder",
    defaultMessage: "Select Route",
    description: "Select Route Placeholder for Add To Route modal",
  },
  tooManyStopsError: {
    id: "addToRouteModal.error.tooManyEntities",
    defaultMessage: "You can only add {max} {max, plural, one {stop} other {stops}} to a route",
    description: "To Many Entities error message on an Add To Route modal",
  },
});

interface AddToRouteModalProps {
  adding: boolean;
  addToRoute: typeof addToRoute.request;
  analyticIssuer?: AnalyticsService;
  autoAssignOnCreate?: boolean;
  createRouteModalVisible: boolean;
  entities: Array<Company | Person>;
  isMmcGeocodeLimitReached: boolean;
  isOrgGeocodeLimitReached: boolean;
  onHide: (added: boolean) => void;
  onSearch: typeof filterRoutes.request;
  routes: Route[];
  selectedRouteId?: Route["id"];
  selectRoute: typeof selectRoute;
  showCreateRouteModal: typeof showCreateRouteModal;
  visible: boolean;
}

const AddToRouteModal = ({
  adding,
  addToRoute,
  analyticIssuer,
  autoAssignOnCreate,
  createRouteModalVisible,
  entities,
  initialize,
  initializing,
  isMmcGeocodeLimitReached,
  isOrgGeocodeLimitReached,
  onHide,
  onSearch,
  routes,
  selectedRouteId,
  selectRoute,
  showCreateRouteModal,
  visible,
}: AddToRouteModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  useEffect(() => {
    if (!visible) {
      return;
    }

    // if this dialog is somehow opened with more than MAX_ROUTE_STOPS entities, show error message and close
    if (entities.length > MAX_ROUTE_STOPS) {
      notification.error({
        message: intl.formatMessage(messages.tooManyStopsError, { max: MAX_ROUTE_STOPS }),
      });
      onHide(false);
    } else {
      selectRoute(undefined);
      initialize({ autoAssignOnCreate, entities });
    }
  }, [entities, autoAssignOnCreate, initialize, intl, onHide, selectRoute, visible]);

  const handleCancelClick = useCallback(() => onHide(false), [onHide]);
  const handleCreateRouteClick = useCallback(() => {
    if (isMmcGeocodeLimitReached || isOrgGeocodeLimitReached) {
      showGeocodeLimitAlertModal(intl, isMmcGeocodeLimitReached);
    } else {
      showCreateRouteModal();
    }
    analyticIssuer?.clicked(["Add To Route", "Create New Route"]);
  }, [
    analyticIssuer,
    intl,
    isMmcGeocodeLimitReached,
    isOrgGeocodeLimitReached,
    showCreateRouteModal,
  ]);

  const handleAddToRoute = useCallback(() => {
    addToRoute({ callback: () => onHide(true), entities });
    analyticIssuer?.clicked(["Add To Route", "Add To Route"]);
  }, [addToRoute, analyticIssuer, entities, onHide]);

  const selectedRoute = routes.find(({ id }) => id === selectedRouteId);
  const isTooManyStops = selectedRoute
    ? selectedRoute.items + entities.length > MAX_ROUTE_STOPS
    : false;

  const handleRouteCreation = useCallback(() => {
    if (autoAssignOnCreate) {
      onHide?.(true);
    }
  }, [autoAssignOnCreate, onHide]);

  const handleSearch = useDebouncedCallback(
    [
      (value: string) => {
        onSearch({ query: value });
      },
      500,
    ],
    [onSearch]
  );

  return (
    <Modal
      okButtonProps={{
        disabled: !selectedRouteId || adding || isTooManyStops,
        icon: <FontAwesomeIcon icon={faRoute} />,
        loading: adding,
      }}
      okText={intl.formatMessage(messages.add)}
      onCancel={handleCancelClick}
      onOk={handleAddToRoute}
      open={visible}
      title={intl.formatMessage(messages.header)}
    >
      <div>
        <SelectField<Route["id"]>
          dropdownMatchSelectWidth={false}
          error={
            isTooManyStops
              ? intl.formatMessage(messages.tooManyStopsError, { max: MAX_ROUTE_STOPS })
              : undefined
          }
          filterOption={(input, option) =>
            (option?.label as string).toLowerCase().includes(input.toLowerCase())
          }
          loading={initializing}
          onChange={selectRoute}
          onSearch={handleSearch}
          options={routes.map(itemToValueLabel)}
          placeholder={intl.formatMessage(messages.placeholder)}
          showSearch
          value={selectedRouteId}
        />
        <Button onClick={handleCreateRouteClick} size="small" type="link">
          {intl.formatMessage(messages.create)}
        </Button>
      </div>
      {createRouteModalVisible && (
        <CreateRouteModal onCreated={handleRouteCreation} routes={routes} />
      )}
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  adding: isAdding(state),
  createRouteModalVisible: isCreateRouteModalVisible(state),
  initializing: isInitializing(state),
  isMmcGeocodeLimitReached: isGeocodeMmcLimitReached(state),
  isOrgGeocodeLimitReached: isGeocodeOrgLimitReached(state),
  routes: getRoutes(state),
  selectedRouteId: getSelectedRouteId(state),
});

const mapDispatchToProps = {
  addToRoute: addToRoute.request,
  initialize: initialize.request,
  onSearch: filterRoutes.request,
  selectRoute,
  showCreateRouteModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddToRouteModal);
